import React from 'react';
import ModalStylized from '../ModalStylized/ModalStylized';
import { PageConfig, CommonPageId } from './PageTypes';
import { getPageComponent } from './PageRegistryCore';
import { NAV_BAR_HEIGHT_PX } from '@mental-mint/shared-utils';

interface PageProps {
  /** Page identifier */
  pageId: string | CommonPageId;
  
  /** Page configuration */
  config: PageConfig;
  
  /** Function called when page should be closed */
  onRequestClose?: () => void;
  
  /** Navigation function for inter-page navigation */
  navigateToPage?: (pageId: string | CommonPageId, navigationProps?: any) => void;
  
  /** Additional props passed during navigation */
  navigationProps?: any;
  
  /** Whether the game has a navigation bar (affects mobile modal height) */
  hasNavBar?: boolean;
}

/**
 * Page component that renders page content based on configuration.
 * Automatically handles modal vs full-screen rendering and integrates with PageRegistry.
 */
export const Page: React.FC<PageProps> = ({
  pageId,
  config,
  onRequestClose,
  navigateToPage,
  navigationProps,
  hasNavBar = true
}) => {
  // Get the page component from the registry
  const renderPageContent = () => {
    const pageRenderer = getPageComponent(pageId);
    
    return pageRenderer({
      onRequestClose,
      pageId,
      navigateToPage,
      navigationProps
    });
  };  
  
  // Modal rendering for overlay pages
  if (config.modalPageConfig) {
    return (
      <ModalStylized
        isOpen={true}
        onRequestClose={onRequestClose!}
        iconImage={config.modalPageConfig.headerImage}
        title={config.modalPageConfig.humanReadableTitle || ""}
        hasNavBar={hasNavBar}
      >
        {renderPageContent()}
      </ModalStylized>
    );
  }

  // Full-screen rendering for background pages
  return (
    <div className="page-fullscreen" style={{ 
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: hasNavBar ? `${NAV_BAR_HEIGHT_PX}px` : 0,
      zIndex: 1,
      overflowY: 'auto',
      overflowX: 'hidden'
    }}>
      {renderPageContent()}
    </div>
  );
};
